import { Dialog } from '@base-ui/react';
import cx from 'classnames';
import { format } from 'date-fns';
import { Button, ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { toShortDateFormat } from '@shared/utils/dateFormatters';
import { useAuth } from 'restaurantAdmin/context/AuthContext';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  RESERVATIONS_COVERS_REPORT_PATH,
  RESERVATIONS_RESERVATIONS_REPORT_PATH,
  SETTINGS_REPORTS_PATH,
} from 'restaurantAdmin/paths';
import typography from '~styles/typography.scss';
import { DATE_PARAM_FORMAT } from '../../utils/constants';
import { useOperaCovers } from './hotelLink/hooks';
import { useReservationServiceContext } from './state/ReservationServiceContext';
import styles from './StatsDrawer.scss';
import { type GuestCounts, type ReservationCounts } from './apiHelpers';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getServiceStats } from './apiHelpers';

const StatsHeaderRow = ({ label, count }: { label: string; count: number }) => (
  <div className={styles.statsRow}>
    <h6 className={typography.h6}>{label}</h6>
    <div className={styles.statsValues}>
      <span className={typography.c3}>All Day</span>
      <span className={typography.h6}>{count}</span>
    </div>
  </div>
);

const StatsDataRow = ({
  label,
  percentage,
  count,
}: {
  label: string;
  percentage: number;
  count: number;
}) => (
  <div className={styles.statsRow}>
    <span className={typography.c2}>{label}</span>
    <div className={styles.statsValues}>
      <span className={cx(typography.c3, styles.percentage)}>
        {percentage}%
      </span>
      <span className={typography.c2}>{count}</span>
    </div>
  </div>
);

const calculateReservationPercentages = (counts: ReservationCounts) =>
  counts.total > 0
    ? {
        finished: Math.ceil((counts.finished / counts.total) * 100),
        upcoming: Math.ceil((counts.upcoming / counts.total) * 100),
        noShow: Math.ceil((counts.noShow / counts.total) * 100),
        canceled: Math.ceil((counts.canceled / counts.total) * 100),
      }
    : { finished: 0, upcoming: 0, noShow: 0, canceled: 0 };

const calculateGuestPercentages = (counts: GuestCounts) =>
  counts.total > 0
    ? {
        finished: Math.ceil((counts.finished / counts.total) * 100),
        upcoming: Math.ceil((counts.upcoming / counts.total) * 100),
        seated: Math.ceil((counts.seated / counts.total) * 100),
        noShow: Math.ceil((counts.noShow / counts.total) * 100),
        canceled: Math.ceil((counts.canceled / counts.total) * 100),
      }
    : { finished: 0, upcoming: 0, seated: 0, noShow: 0, canceled: 0 };

const coverPercentage = (count: number, total: number): number =>
  total > 0 ? Math.ceil((count / total) * 100) : 0;

export const StatsDrawer = () => {
  const { selectedDate } = useReservationServiceContext();
  const formattedDate = format(selectedDate, DATE_PARAM_FORMAT);
  const {
    id: restaurantId,
    trafficReportUrl,
    metabaseDashboardId,
    hotelIntegrationEnabled,
  } = useRestaurant();
  const { isManager } = useAuth();

  const { data: serviceStats } = useQuery({
    queryKey: ['restaurants', restaurantId, 'service-stats', formattedDate],
    queryFn: async () => getServiceStats(restaurantId, formattedDate),
  });

  const { data: covers } = useOperaCovers(
    formattedDate,
    isManager && hotelIntegrationEnabled,
  );

  const displayDate = toShortDateFormat(selectedDate, false);

  if (!serviceStats) {
    return null;
  }

  const { reservationCounts, guestCounts, guestCountsByServer } = serviceStats;
  const reservationPercentages =
    calculateReservationPercentages(reservationCounts);
  const guestPercentages = calculateGuestPercentages(guestCounts);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Icon name="stats" />
          <h4 className={typography.h4}>Service Stats - {displayDate}</h4>
        </div>

        <Dialog.Close
          render={({ onClick }) => (
            <Button
              label="Close"
              onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
              variant={ButtonVariants.Tertiary}
            />
          )}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.statsContainer} data-testid="reservations-stats">
          <StatsHeaderRow
            label="Reservations"
            count={reservationCounts.total}
          />
          <StatsDataRow
            label="Finished"
            percentage={reservationPercentages.finished}
            count={reservationCounts.finished}
          />
          <StatsDataRow
            label="Upcoming"
            percentage={reservationPercentages.upcoming}
            count={reservationCounts.upcoming}
          />
          <StatsDataRow
            label="No Show"
            percentage={reservationPercentages.noShow}
            count={reservationCounts.noShow}
          />
          <StatsDataRow
            label="Canceled"
            percentage={reservationPercentages.canceled}
            count={reservationCounts.canceled}
          />
          <div className={styles.print}>
            <button
              onClick={() =>
                window
                  .open(
                    `${RESERVATIONS_RESERVATIONS_REPORT_PATH}?date=${formattedDate}`,
                    '_blank',
                    'noopener,noreferrer',
                  )
                  ?.focus()
              }
              type="button"
            >
              <Icon name="printer" />
              <span className={typography.c3}>
                Print Reservation List for {displayDate}
              </span>
            </button>
          </div>
        </div>

        <div className={styles.statsContainer}>
          <StatsHeaderRow label="Guests" count={guestCounts.total} />
          <StatsDataRow
            label="Seated"
            percentage={guestPercentages.seated}
            count={guestCounts.seated}
          />
          <StatsDataRow
            label="Finished"
            percentage={guestPercentages.finished}
            count={guestCounts.finished}
          />
          <StatsDataRow
            label="Upcoming"
            percentage={guestPercentages.upcoming}
            count={guestCounts.upcoming}
          />
          <StatsDataRow
            label="No Show"
            percentage={guestPercentages.noShow}
            count={guestCounts.noShow}
          />
          <StatsDataRow
            label="Canceled"
            percentage={guestPercentages.canceled}
            count={guestCounts.canceled}
          />
          <div className={styles.print}>
            <button
              onClick={() =>
                window
                  .open(
                    `${RESERVATIONS_COVERS_REPORT_PATH}?date=${formattedDate}`,
                    '_blank',
                    'noopener,noreferrer',
                  )
                  ?.focus()
              }
              type="button"
            >
              <Icon name="printer" />
              <span className={typography.c3}>
                Print Guest List for {displayDate}
              </span>
            </button>
          </div>
        </div>

        {isManager && hotelIntegrationEnabled && covers && (
          <div className={styles.statsContainer} data-testid="hotel-stats">
            <StatsHeaderRow label="Covers" count={covers.totalCovers} />
            <StatsDataRow
              label="Hotel guests"
              percentage={coverPercentage(
                covers.hotelGuestCovers,
                covers.totalCovers,
              )}
              count={covers.hotelGuestCovers}
            />
            <StatsDataRow
              label="In house"
              percentage={coverPercentage(
                covers.inHouseCovers,
                covers.totalCovers,
              )}
              count={covers.inHouseCovers}
            />
          </div>
        )}

        <div className={styles.statsContainer} data-testid="servers-stats">
          <div className={styles.statsRow}>
            <h6 className={typography.h6}>Server Name</h6>
            <div className={styles.statsValues}>
              <span className={typography.c3}>Active</span>
              <span className={typography.c3}>Total</span>
            </div>
          </div>

          {Object.entries(guestCountsByServer).map(
            ([serverId, serverDetails]) => (
              <div className={styles.statsRow} key={serverId}>
                <span className={typography.c2}>
                  {serverDetails.serverName}
                </span>

                <div className={styles.statsValues}>
                  <span className={typography.c3}>
                    {serverDetails.activeGuestCount}
                  </span>

                  <span className={typography.c3}>
                    {serverDetails.totalGuestCount}
                  </span>
                </div>
              </div>
            ),
          )}
        </div>

        {(trafficReportUrl || metabaseDashboardId) && (
          <div className={styles.statsContainer} data-testid="servers-stats">
            <div className={styles.statsRow}>
              <h6 className={typography.h6}>Reports</h6>
            </div>

            <div className={styles.statsRow}>
              <Link
                className={cx(typography.c2, styles.reportLink)}
                to={SETTINGS_REPORTS_PATH}
              >
                Restaurant Reports
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
